/*
 * The group picker's list, read once by every form that offers groups as owners — an AI chat's, a
 * shared drive's. Each area filters it by its own rule (who may own a chat, which groups a drive may
 * be given); the reading, and when it is read again, is the same for all of them, so it lives here.
 */

import { useEffect, useState } from "react"
import { useModel } from "../shell/index.ts"
import type { GroupView } from "../store/model.ts"
import { groupsApi } from "./api.ts"

/**
 * The picker's list of groups, or null until it first answers. It is read again when the model's
 * groups change — a group created, renamed or given members elsewhere — so what is on offer follows
 * the directory while a form or a panel stays open. A failed read keeps the last answer: the caller
 * then says nothing it cannot know rather than an error on a page that otherwise works.
 */
export function usePickableGroups(): GroupView[] | null {
	const known = useModel((model) => model.groups)
	const [listed, setListed] = useState<GroupView[] | null>(null)
	useEffect(() => {
		let alive = true
		groupsApi
			.listPickable()
			.then((answer) => {
				if (alive) setListed(answer)
			})
			.catch(() => undefined)
		return () => {
			alive = false
		}
	}, [known])
	return listed
}
